import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FeatherLoader, FeatherArrowLeft, FeatherFileSpreadsheet } from "@subframe/core";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Button } from "../../../../ui/components/Button";
import { useAuth } from "../../../auth";
import {
  getRegisterFile,
  RegisterFileData,
  RegisterExcelSheet,
} from "../../data/api/pivotApi";

// Helper: Generate period tabs from date range
const generatePeriodTabs = (fromDate: string, toDate: string): { key: string; label: string }[] => {
  const tabs: { key: string; label: string }[] = [];
  
  try {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    // Iterate through each month in the range
    const current = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);
    
    while (current <= end) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthName = monthNames[current.getMonth()];
      const year = current.getFullYear();
      const yearShort = String(year).slice(-2);
      
      tabs.push({
        key: `${monthName.toLowerCase()}-${yearShort}`, // e.g., "apr-25"
        label: `${monthName} ${year}`, // e.g., "Apr 2025"
      });
      
      current.setMonth(current.getMonth() + 1);
    }
  } catch (e) {
    console.error("Error generating period tabs:", e);
  }
  
  return tabs;
};

export const RegisterViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [searchParams] = useSearchParams();
  
  // URL params
  const lead = searchParams.get("lead") || "";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";
  const periodValuesParam = searchParams.get("periodValues") || "";
  
  // Parse period values from URL (base64 encoded JSON)
  const periodValues = useMemo<Record<string, number>>(() => {
    if (!periodValuesParam) return {};
    try {
      const decoded = atob(periodValuesParam);
      return JSON.parse(decoded);
    } catch (e) {
      console.error("Failed to parse period values:", e);
      return {};
    }
  }, [periodValuesParam]);
  
  // Helper: Match period tab key to period values key
  const getPeriodValue = (tabLabel: string): number | null => {
    // tabLabel is like "Apr 2025"
    // periodValues keys are like "Apr - 2025" or "Apr-2025" or "Apr 2025"
    for (const [key, value] of Object.entries(periodValues)) {
      // Normalize: remove spaces and dashes
      const normalizedKey = key.replace(/[\s-]+/g, "").toLowerCase();
      const normalizedLabel = tabLabel.replace(/[\s-]+/g, "").toLowerCase();
      if (normalizedKey === normalizedLabel) {
        return value;
      }
    }
    return null;
  };
  
  // Calculate total across all periods
  const totalValue = useMemo(() => {
    return Object.values(periodValues).reduce((sum, val) => sum + val, 0);
  }, [periodValues]);
  
  // State
  const [activePeriod, setActivePeriod] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [registerData, setRegisterData] = useState<RegisterFileData | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>("");
  
  // Generate period tabs from date range
  const periodTabs = useMemo(() => {
    return generatePeriodTabs(fromDate, toDate);
  }, [fromDate, toDate]);
  
  // Set initial active period
  useEffect(() => {
    if (periodTabs.length > 0 && !activePeriod) {
      setActivePeriod(periodTabs[0].key);
    }
  }, [periodTabs, activePeriod]);
  
  // Load register data when period changes
  useEffect(() => {
    if (lead && activePeriod) {
      loadRegisterData(activePeriod);
    }
  }, [lead, activePeriod]);
  
  const loadRegisterData = async (period: string) => {
    setLoading(true);
    setError("");
    setRegisterData(null);
    
    try {
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
      const data = await getRegisterFile(lead, period, companyId);
      
      if (data) {
        setRegisterData(data);
        if (data.sheetNames.length > 0) {
          setActiveSheet(data.sheetNames[0]);
        }
      } else {
        setError(`No register found for "${lead}" in ${period}`);
      }
    } catch (err: any) {
      console.error("Failed to load register:", err);
      setError(err.message || "Failed to load register");
    } finally {
      setLoading(false);
    }
  };
  
  // Get current sheet data
  const currentSheet: RegisterExcelSheet | null = registerData?.sheets[activeSheet] || null;
  
  // Helper: Check if a value is numeric
  const isNumeric = (value: any): boolean => {
    if (value === null || value === undefined || value === "") return false;
    if (typeof value === "number") return true;
    if (typeof value === "string") {
      // Remove commas and check if it's a valid number
      const cleaned = value.replace(/,/g, "").trim();
      return !isNaN(parseFloat(cleaned)) && isFinite(parseFloat(cleaned));
    }
    return false;
  };

  // Helper: Get numeric value from cell
  const getNumericValue = (value: any): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  // Identify numeric columns (columns that contain numeric values)
  const numericColumns = useMemo<string[]>(() => {
    if (!currentSheet || currentSheet.rows.length === 0) return [];
    
    const columnMap = new Map<string, boolean>();
    
    // Check all rows (skip header row at index 0)
    for (let i = 1; i < currentSheet.rows.length; i++) {
      const row = currentSheet.rows[i];
      row.forEach((cell) => {
        if (isNumeric(cell.value)) {
          columnMap.set(cell.col, true);
        }
      });
    }
    
    return Array.from(columnMap.keys());
  }, [currentSheet]);

  // Calculate column totals
  const columnTotals = useMemo<Record<string, number>>(() => {
    if (!currentSheet || numericColumns.length === 0) return {};
    
    const totals: Record<string, number> = {};
    
    // Initialize totals for all numeric columns
    numericColumns.forEach((col) => {
      totals[col] = 0;
    });
    
    // Sum values for each numeric column (skip header row at index 0)
    for (let i = 1; i < currentSheet.rows.length; i++) {
      const row = currentSheet.rows[i];
      row.forEach((cell) => {
        if (numericColumns.includes(cell.col) && isNumeric(cell.value)) {
          totals[cell.col] += getNumericValue(cell.value);
        }
      });
    }
    
    return totals;
  }, [currentSheet, numericColumns]);

  // Get active period databook total
  const activePeriodDatabookTotal = useMemo<number | null>(() => {
    const activeTab = periodTabs.find((tab) => tab.key === activePeriod);
    if (activeTab) {
      return getPeriodValue(activeTab.label);
    }
    return null;
  }, [activePeriod, periodTabs, periodValues]);

  // Get primary numeric column (first numeric column, or "Gross" if exists)
  const primaryNumericColumn = useMemo<string | null>(() => {
    if (!currentSheet || numericColumns.length === 0 || !currentSheet.rows[0]) return null;
    
    // Check if "Gross" column exists in header row
    const headerRow = currentSheet.rows[0];
    const grossCol = headerRow.find((cell) => {
      const cellValue = String(cell.value || "").toLowerCase();
      return cellValue.includes("gross") && numericColumns.includes(cell.col);
    });
    if (grossCol) {
      return grossCol.col;
    }
    
    // Otherwise, return first numeric column
    return numericColumns[0] || null;
  }, [currentSheet, numericColumns]);

  // Calculate difference for primary column
  const primaryColumnDifference = useMemo<number | null>(() => {
    if (!primaryNumericColumn || activePeriodDatabookTotal === null) return null;
    const registerTotal = columnTotals[primaryNumericColumn] || 0;
    return registerTotal - activePeriodDatabookTotal;
  }, [primaryNumericColumn, columnTotals, activePeriodDatabookTotal]);
  
  // Format cell value
  const formatCellValue = (value: string | number | null): string => {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    if (typeof value === "number") {
      return new Intl.NumberFormat("en-IN").format(value);
    }
    return String(value);
  };
  
  // Extract display name from lead path
  const leadParts = lead.split(" > ");
  const displayName = leadParts[leadParts.length - 1] || lead;
  
  return (
    <DefaultPageLayout>
      <div className="flex flex-col w-full h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-border bg-green-50">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => window.close()}
            >
              Close
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-green-100">
                <FeatherFileSpreadsheet className="text-green-600" />
              </div>
              <div className="flex flex-col">
                <h1 className="text-heading-2 font-heading-2 text-default-font">
                  Register: {displayName}
                </h1>
                <span className="text-caption font-caption text-subtext-color">
                  {lead}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Period Summary & Tabs */}
        <div className="flex flex-col border-b border-neutral-border bg-neutral-50">
          {/* Total Summary Row */}
          {Object.keys(periodValues).length > 0 && (
            <div className="flex items-center gap-4 px-6 py-2 border-b border-neutral-200 bg-green-50">
              <span className="text-body font-body text-subtext-color">
                Databook Totals:
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {periodTabs.map((tab) => {
                  const value = getPeriodValue(tab.label);
                  return (
                    <div
                      key={tab.key}
                      className={`flex flex-col items-center px-3 py-1 rounded-md ${
                        activePeriod === tab.key
                          ? "bg-green-600 text-white"
                          : "bg-white border border-neutral-200"
                      }`}
                    >
                      <span className={`text-caption font-caption ${
                        activePeriod === tab.key ? "text-green-100" : "text-subtext-color"
                      }`}>
                        {tab.label}
                      </span>
                      <span className={`text-body-bold font-body-bold ${
                        activePeriod === tab.key ? "text-white" : "text-default-font"
                      }`}>
                        {value !== null ? new Intl.NumberFormat("en-IN").format(value) : "-"}
                      </span>
                    </div>
                  );
                })}
                <div className="flex flex-col items-center px-3 py-1 rounded-md bg-green-100 border border-green-300">
                  <span className="text-caption font-caption text-green-700">
                    Total
                  </span>
                  <span className="text-body-bold font-body-bold text-green-800">
                    {new Intl.NumberFormat("en-IN").format(totalValue)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Period Tabs */}
          <div className="flex items-center gap-1 px-6 py-2 border-b border-neutral-border bg-neutral-50 overflow-x-auto">
            {periodTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActivePeriod(tab.key)}
                className={`px-4 py-2 text-body font-body rounded-t-md transition-colors whitespace-nowrap ${
                  activePeriod === tab.key
                    ? "bg-white text-brand-600 border border-b-0 border-neutral-border font-bold"
                    : "text-subtext-color hover:bg-neutral-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Sheet Tabs */}
        {registerData && registerData.sheetNames.length > 0 && (
          <div className="flex items-center gap-1 px-6 py-2 border-b border-neutral-border bg-neutral-50 overflow-x-auto">
            {registerData.sheetNames.map((sheetName) => (
              <button
                key={sheetName}
                onClick={() => {
                  setActiveSheet(sheetName);
                }}
                className={`px-4 py-2 text-body font-body rounded-t-md transition-colors whitespace-nowrap ${
                  activeSheet === sheetName
                    ? "bg-white text-brand-600 border border-b-0 border-neutral-border font-bold"
                    : "text-subtext-color hover:bg-neutral-100"
                }`}
              >
                {sheetName}
              </button>
            ))}
          </div>
        )}
        
        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-white">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-3 text-subtext-color">
                <FeatherLoader className="animate-spin" />
                <span>Loading register for {activePeriod}...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="text-error-600 text-center">{error}</div>
              <span className="text-caption text-subtext-color">
                Try selecting a different period
              </span>
            </div>
          ) : currentSheet ? (
            <table className="border-collapse min-w-full">
              {/* Column headers */}
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 z-30 px-2 py-2 text-center text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    #
                  </th>
                  {currentSheet.rows[0]?.map((cell) => (
                    <th
                      key={cell.col}
                      className="px-4 py-2 text-center text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300 min-w-[120px]"
                    >
                      {cell.col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentSheet.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-neutral-50">
                    <td className="sticky left-0 z-10 px-2 py-2 text-center text-caption font-caption text-subtext-color bg-neutral-100 border border-neutral-200 min-w-[50px]">
                      {rowIndex + 1}
                    </td>
                    {row.map((cell) => (
                      <td
                        key={`${cell.col}${cell.row}`}
                        className={`px-4 py-2 text-body font-body border border-neutral-200 min-w-[120px] max-w-[300px] truncate ${
                          cell.formula ? "bg-blue-50" : ""
                        }`}
                        title={String(cell.value || "")}
                      >
                        {formatCellValue(cell.value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {/* Summary Row with Totals */}
              {numericColumns.length > 0 && (
                <tfoot className="sticky bottom-0 z-10">
                  <tr className="bg-neutral-100 border-t-2 border-neutral-400">
                    <td className="sticky left-0 z-20 px-2 py-3 text-center text-body-bold font-body-bold text-default-font bg-neutral-200 border border-neutral-300 min-w-[50px]">
                      Total
                    </td>
                    {currentSheet.rows[0]?.map((headerCell) => {
                      const isNumericCol = numericColumns.includes(headerCell.col);
                      const total = columnTotals[headerCell.col] || 0;
                      const isPrimary = headerCell.col === primaryNumericColumn;
                      const difference = isPrimary ? primaryColumnDifference : null;
                      
                      return (
                        <td
                          key={headerCell.col}
                          className={`px-4 py-3 text-body-bold font-body-bold border border-neutral-300 min-w-[120px] ${
                            isNumericCol ? "text-right" : "text-center"
                          } ${
                            isPrimary && difference !== null
                              ? Math.abs(difference) < 0.01
                                ? "bg-green-50 text-green-800"
                                : activePeriodDatabookTotal !== null && activePeriodDatabookTotal !== 0 && Math.abs(difference) < activePeriodDatabookTotal * 0.01
                                ? "bg-amber-50 text-amber-800"
                                : "bg-red-50 text-red-800"
                              : "bg-neutral-200 text-default-font"
                          }`}
                        >
                          {isNumericCol ? (
                            <div className="flex flex-col items-end gap-1">
                              <span>{formatCellValue(total)}</span>
                              {isPrimary && difference !== null && activePeriodDatabookTotal !== null && (
                                <span className={`text-caption font-caption ${
                                  Math.abs(difference) < 0.01
                                    ? "text-green-700"
                                    : Math.abs(difference) < (activePeriodDatabookTotal || 0) * 0.01
                                    ? "text-amber-700"
                                    : "text-red-700"
                                }`}>
                                  {difference > 0 ? "+" : ""}{formatCellValue(difference)}
                                </span>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-subtext-color">
                Select a period to view register data
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex flex-col gap-2 px-6 py-3 border-t border-neutral-border bg-neutral-50">
          <div className="flex items-center justify-between">
            <span className="text-caption font-caption text-subtext-color">
              {registerData?.fileName || "No file loaded"}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              {currentSheet
                ? `${currentSheet.maxRow} rows × ${currentSheet.maxCol} columns`
                : ""}
            </span>
          </div>
          {/* Summary Comparison */}
          {primaryNumericColumn && activePeriodDatabookTotal !== null && (
            <div className="flex items-center gap-4 pt-2 border-t border-neutral-200">
              <div className="flex items-center gap-2">
                <span className="text-caption font-caption text-subtext-color">Register Total:</span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {formatCellValue(columnTotals[primaryNumericColumn] || 0)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-caption font-caption text-subtext-color">Databook Total:</span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {formatCellValue(activePeriodDatabookTotal)}
                </span>
              </div>
              {primaryColumnDifference !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-caption font-caption text-subtext-color">Difference:</span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      Math.abs(primaryColumnDifference) < 0.01
                        ? "text-green-700"
                        : activePeriodDatabookTotal !== null && activePeriodDatabookTotal !== 0 && Math.abs(primaryColumnDifference) < activePeriodDatabookTotal * 0.01
                        ? "text-amber-700"
                        : "text-red-700"
                    }`}
                  >
                    {primaryColumnDifference > 0 ? "+" : ""}
                    {formatCellValue(primaryColumnDifference)}
                  </span>
                  {Math.abs(primaryColumnDifference) < 0.01 ? (
                    <span className="text-caption text-green-700">✓ Match</span>
                  ) : activePeriodDatabookTotal !== null && activePeriodDatabookTotal !== 0 && Math.abs(primaryColumnDifference) < activePeriodDatabookTotal * 0.01 ? (
                    <span className="text-caption text-amber-700">⚠ Small variance</span>
                  ) : (
                    <span className="text-caption text-red-700">⚠ Variance</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

